import AlertOctagon from "lucide-react/dist/esm/icons/alert-octagon";
import AlertTriangle from "lucide-react/dist/esm/icons/alert-triangle";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2";
import Info from "lucide-react/dist/esm/icons/info";
import type { HTMLAttributes, ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { useUser } from "@/context/user-provider";

import { Text } from "./Text";

const banner = tv({
  base: "flex items-start gap-3 rounded-lg border px-4 py-3",
  variants: {
    tone: {
      info: "bg-info-background border-info-surface",
      success: "bg-success-background border-success-surface",
      warning: "bg-warning-background border-warning-surface",
      critical: "bg-destructive-background border-destructive-surface",
    },
  },
  defaultVariants: {
    tone: "info",
  },
});

type Tone = NonNullable<VariantProps<typeof banner>["tone"]>;

const toneIcon: Record<Tone, React.ReactNode> = {
  info: <Info className="h-4 w-4 text-[#0e4d6f]" />,
  success: <CheckCircle2 className="text-success h-4 w-4" />,
  warning: <AlertTriangle className="text-warning h-4 w-4" />,
  critical: <AlertOctagon className="text-destructive h-4 w-4" />,
};

type Props = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof banner> & {
    title?: ReactNode;
    action?: ReactNode;
  };

const BannerComponent: React.FC<Props> = ({
  tone = "info",
  title,
  action,
  className,
  children,
  ...props
}) => {
  return (
    <div
      role={tone === "critical" ? "alert" : "status"}
      className={banner({ tone, className })}
      {...props}
    >
      <div className="mt-0.5 shrink-0">{toneIcon[tone]}</div>
      <div className="flex flex-1 flex-col gap-1">
        {title && <p className="text-text text-sm font-semibold">{title}</p>}
        {children && (
          <Text variant="bodyMd" tone="default">
            {children}
          </Text>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
};

const BannerSuperAdmin: React.FC<Props> = (props) => {
  const { isSuperAdmin } = useUser();

  if (!isSuperAdmin) {
    return null;
  }

  return <BannerComponent {...props} />;
};

export const Banner = Object.assign(BannerComponent, {
  SuperAdmin: BannerSuperAdmin,
});
